/**
 * Helpers for deriving the "pending cancellation" UI state from a Stripe subscription.
 *
 * Stripe only sets `canceled_at` for `cancel_at_period_end` cancellations — it is NOT set when a
 * subscription is scheduled to end via a future-dated `cancel_at` (which the backend uses for
 * installment and discount/trial cancellations). Detecting a pending cancellation purely from
 * `canceled_at` therefore hides those cases entirely, so we also look at `cancel_at` /
 * `cancel_at_period_end` and treat `canceled_at` as optional.
 */

/** Minimal slice of the Stripe Subscription object that the cancellation UI relies on. */
export interface StripeSubscriptionCancellationFields {
	status?: string;
	cancel_at_period_end?: boolean | null;
	/** Unix timestamp (seconds) at which the subscription will be canceled. */
	cancel_at?: number | null;
	/** Unix timestamp (seconds) at which cancellation was requested. Only set for cancel_at_period_end. */
	canceled_at?: number | null;
	items?: {
		data?: Array<{
			/** Unix timestamp (seconds) for the end of the current billing period. */
			current_period_end?: number | null;
		}>;
	};
}

export type CancellationReason = "billing_cycle" | "discount" | "trial";

/**
 * The cancellation as recorded on our own subscription row.
 *
 * The Stripe object above reaches the browser through a per-process in-memory cache on the API, so
 * after a cancellation only the container that handled it serves fresh data — every other one
 * keeps returning a pre-cancellation object until the TTL expires, and the notice vanishes on
 * refresh. These fields are written by our own cancel path and are identical in every container,
 * so they are the authoritative fallback (PREC-3922).
 */
export interface LocalCancellationFields {
	/** ISO date access ends. */
	endedAt?: string | Date | null;
	/** ISO date the cancellation was requested. */
	canceledAt?: string | Date | null;
}

const toDate = (value: string | Date | null | undefined): Date | null => {
	if (!value) {
		return null;
	}

	const date = value instanceof Date ? value : new Date(value);

	return Number.isFinite(date.getTime()) ? date : null;
};

export interface CancellationInfo {
	reason: CancellationReason;
	/** Formatted date the cancellation was requested, or null when Stripe did not provide it. */
	canceledAtFormatted: string | null;
	/** Formatted date access ends, or null when it cannot be determined. */
	cancelAtFormatted: string | null;
}

export interface CancellationNotice {
	key: string;
	params: Record<string, string>;
}

const NOTICE_KEY_BASE = "usage.subscription.overview.";

// A cancel_at more than a day past the billing-period end indicates the end is driven by a
// discount/commitment period rather than the normal billing cycle.
const DISCOUNT_THRESHOLD_MS = 86_400_000;

/** Notice key per reason when both the cancellation date and the end date are known. */
const DATED_NOTICE_KEYS: Record<CancellationReason, string> = {
	billing_cycle: "cancellationBilling",
	discount: "cancellationDiscount",
	trial: "cancellationTrial",
};

/** Notice key per reason when only the end date is known. */
const SCHEDULED_NOTICE_KEYS: Record<CancellationReason, string> = {
	billing_cycle: "cancellationBillingScheduled",
	discount: "cancellationDiscountScheduled",
	trial: "cancellationTrialScheduled",
};

const formatCancellationDate = (date: Date): string =>
	date.toLocaleDateString("de-DE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});

/**
 * Derive pending-cancellation display info from a Stripe subscription, or null when the
 * subscription is fully active (no pending cancellation) or already fully canceled.
 */
export const getCancellationInfo = (
	subscription: StripeSubscriptionCancellationFields | null | undefined,
	local?: LocalCancellationFields | null
): CancellationInfo | null => {
	if (subscription?.status === "canceled") {
		return null;
	}

	const localCanceledAt = toDate(local?.canceledAt);
	const localEndedAt = toDate(local?.endedAt);

	// Our own row counts as a pending cancellation only while access has not actually ended: a
	// past `endedAt` is a finished subscription, not a notice to show.
	const localPending =
		Boolean(localCanceledAt) || Boolean(localEndedAt && localEndedAt.getTime() > Date.now());

	const hasPendingCancellation =
		subscription?.cancel_at_period_end === true ||
		Boolean(subscription?.cancel_at) ||
		Boolean(subscription?.canceled_at) ||
		localPending;

	if (!hasPendingCancellation) {
		return null;
	}

	const currentPeriodEndUnix = subscription?.items?.data?.[0]?.current_period_end;
	const currentPeriodEnd = currentPeriodEndUnix ? new Date(currentPeriodEndUnix * 1000) : null;

	// Access ends at the explicit cancel_at, otherwise the end of the current billing period, and
	// failing both at the `endedAt` our own cancel path recorded.
	const cancelAt = subscription?.cancel_at
		? new Date(subscription.cancel_at * 1000)
		: subscription?.cancel_at_period_end
			? (currentPeriodEnd ?? localEndedAt)
			: localEndedAt;

	const canceledAt = subscription?.canceled_at
		? new Date(subscription.canceled_at * 1000)
		: localCanceledAt;

	const reason = resolveCancellationReason(subscription?.status, cancelAt, currentPeriodEnd);

	return {
		reason,
		canceledAtFormatted: canceledAt ? formatCancellationDate(canceledAt) : null,
		cancelAtFormatted: cancelAt ? formatCancellationDate(cancelAt) : null,
	};
};

/**
 * A cancellation during the trial ends the subscription when the trial does and never charges, so
 * it gets its own wording — the billing-cycle/discount messages would be misleading there.
 */
const resolveCancellationReason = (
	status: string | undefined,
	cancelAt: Date | null,
	currentPeriodEnd: Date | null
): CancellationReason => {
	if (status === "trialing") {
		return "trial";
	}

	return cancelAt &&
		currentPeriodEnd &&
		cancelAt.getTime() > currentPeriodEnd.getTime() + DISCOUNT_THRESHOLD_MS
		? "discount"
		: "billing_cycle";
};

/**
 * Pick the i18n key + params for the cancellation notice based on which dates are known.
 * Keeps the rich "canceled on X, continues until Y" wording when Stripe provides canceled_at, and
 * falls back to an end-date-only "scheduled to cancel on Y" message when it does not.
 */
export const getCancellationNotice = (info: CancellationInfo): CancellationNotice => {
	const { reason, canceledAtFormatted, cancelAtFormatted } = info;

	if (canceledAtFormatted && cancelAtFormatted) {
		return {
			key: `${NOTICE_KEY_BASE}${DATED_NOTICE_KEYS[reason]}`,
			params: { canceledAt: canceledAtFormatted, cancelAt: cancelAtFormatted },
		};
	}

	if (canceledAtFormatted) {
		return {
			key: `${NOTICE_KEY_BASE}cancellationNoEnd`,
			params: { canceledAt: canceledAtFormatted },
		};
	}

	if (cancelAtFormatted) {
		return {
			key: `${NOTICE_KEY_BASE}${SCHEDULED_NOTICE_KEYS[reason]}`,
			params: { cancelAt: cancelAtFormatted },
		};
	}

	return { key: `${NOTICE_KEY_BASE}cancellationScheduled`, params: {} };
};
